<template>
  <div class="rich-textarea" :class="{ disabled }" @click="focusEditor">
    <div
      ref="editor"
      class="editor ant-input"
      :data-placeholder="placeholder"
      :contenteditable="!disabled"
      role="textbox"
      aria-multiline="true"
      @input="handleInput"
      @keydown="handleKeydown"
      @keydown.enter.stop.prevent="onEnter"
      @keyup.enter.stop
      @paste="handlePaste"
      :style="boxStyle" 
    ></div>

    <span v-if="showCount" class="count">{{ currentLength }}/{{ maxlength }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { toAnchors } from "@/app/platformSettings/utils/linkify";

const props = withDefaults(defineProps<{
  modelValue: string
  placeholder?: string
  maxlength?: number
  rows?: number
  showCount?: boolean
  disabled?: boolean
}>(), {
  modelValue: "",
  placeholder: "",
  maxlength: 980,
  rows: 2,
  showCount: false,
  disabled: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", v: string): void
  (e: "input"): void
}>();

const editor = ref<HTMLDivElement | null>(null);
const updatingFromOutside = ref(false);
const wantLinkify = ref(false);

function toPlainText(html: string) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || "";
}

function renderFromModel(value: string) {
  if (!editor.value) return;
  updatingFromOutside.value = true;
  editor.value.innerHTML = toAnchors(value);
  updatingFromOutside.value = false;
}

onMounted(() => {
  renderFromModel(props.modelValue);
  requestAnimationFrame(() => {
  if (editor.value) {
    editor.value.style.height = `${initialHeightPx.value}px`;
  }
  });
});

watch(() => props.modelValue, (v) => {
  if (updatingFromOutside.value) return;
    const currentPlain = editor.value ? toPlainText(editor.value.innerHTML) : "";
  if (v === currentPlain) return;
    renderFromModel(v);
  if (editor.value) placeCaretAtEnd(editor.value);
});

const currentLength = ref(0);

watch(() => props.modelValue, (v) => {
  currentLength.value = Math.min(v.length, props.maxlength);
}, { immediate: true });

const lineHeight = 22;      
const paddingY = 8;         
const borderY  = 2;         
const oneLine  = lineHeight + paddingY + borderY;

const initialHeightPx = computed(() => {
  const rows = Math.max(1, props.rows);
  return rows * lineHeight + paddingY + borderY;
});

const boxStyle = computed(() => {
  return {
    minHeight: `${oneLine}px`,     
    overflowY: 'auto',
    resize: 'vertical',
    boxSizing: 'border-box',
  } as const;
});

// Guarda la posición del caret
function linkifyNow() {
  if (!editor.value) return;
  const el = editor.value;

  const caret = getCaretOffset(el);

  const plain = toPlainText(el.innerHTML);
  el.innerHTML = toAnchors(plain);

  const newLen = toPlainText(el.innerHTML).length;
  setCaretOffset(el, Math.min(caret ?? newLen, newLen));

  wantLinkify.value = false;
}

function handleInput() {
  if (!editor.value || updatingFromOutside.value) return;

  const el = editor.value;
  let plain = toPlainText(el.innerHTML);

  if (plain.length > props.maxlength) {
    const caret = getCaretOffset(el);
    plain = plain.slice(0, props.maxlength);
    el.textContent = plain;
    setCaretOffset(el, Math.min(caret ?? plain.length, plain.length));
  }

  currentLength.value = Math.min(plain.length, props.maxlength);
  emit("update:modelValue", plain);
  emit("input");

  if (wantLinkify.value) {
    requestAnimationFrame(linkifyNow);
  }
}

function onEnter(e: KeyboardEvent) {
  insertTextAtCursor('\n');
  wantLinkify.value = true;
  handleInput();
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === ' ') wantLinkify.value = true;
}

function handlePaste(e: ClipboardEvent) {
  if (!editor.value) return;
  e.preventDefault();
  const text = (e.clipboardData?.getData("text") ?? "").replace(/\r\n/g, "\n");
  insertTextAtCursor(text);
  wantLinkify.value = true;
  handleInput();
}

function insertTextAtCursor(text: string) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;
  sel.deleteFromDocument();
  sel.getRangeAt(0).insertNode(document.createTextNode(text));
  sel.collapseToEnd();
}

// Mueve el caret al final del contenido del editor.
function placeCaretAtEnd(el: HTMLElement) {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function focusEditor() {
  editor.value?.focus();
}

function isInside(el: HTMLElement, node: Node | null) {
  return !!node && (node === el || el.contains(node));
}

// Calcula el offset de caret
function getCaretOffset(el: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!isInside(el, range.startContainer)) return null;

  const pre = range.cloneRange();
  pre.selectNodeContents(el);
  pre.setEnd(range.startContainer, range.startOffset);
  return pre.toString().length;
}

// Coloca el caret exactamente en el offset pedido.
function setCaretOffset(el: HTMLElement, offset: number) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  let node: Node | null = walker.nextNode();
  let count = 0;

  while (node) {
    const len = (node.textContent ?? "").length;
    if (count + len >= offset) {
      const range = document.createRange();
      range.setStart(node, Math.max(0, offset - count));
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
    count += len;
    node = walker.nextNode();
  }
  placeCaretAtEnd(el);
}

</script>

<style scoped>
.rich-textarea {
  position: relative;
  width: 100%;
}

.editor {
  width: 100%;
  outline: none;
  padding: 4px 11px;
  border: 1px solid var(--ant-color-border, #d9d9d9);
  border-radius: 6px;
  line-height: 22px;
  white-space: pre-wrap;
  word-break: break-word;
  box-sizing: border-box;  
}

.rich-textarea.disabled .editor {
  background: var(--ant-color-bg-container-disabled, #f5f5f5);
  color: var(--ant-color-text-disabled, rgba(0,0,0,0.25));
  pointer-events: none;
}

.editor:focus {
  border-color: var(--ant-color-primary, #1677ff);
  box-shadow: 0 0 0 2px rgba(22,119,255,.2);
}

.editor:empty::before {
  content: attr(data-placeholder);
  color: rgba(0,0,0,.25);
}

.editor :deep(a) {
  color: var(--ant-color-link, #1677ff);
  text-decoration: underline;
  cursor: pointer;
}

.count {
  position: absolute;
  right: 8px;
  bottom: 4px;
  font-size: 12px;
  color: rgba(0,0,0,0.45);
  background: transparent;
}
</style>
